import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getSnapTradeUsers, getUserSnapTradeDetail, deleteUserSnapTrade, getUserAccountBalancesAdmin, getUserAccountActivitiesAdmin } from "../../../api/Admin";
import APIEndpoint from "../../../api/APIEndpoint";
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  VStack,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Collapse,
} from "@chakra-ui/react";
import { SearchIcon, DeleteIcon } from "@chakra-ui/icons";
import moment from "moment";

interface SnapUserRow {
  id: number;
  email: string;
  lastActive?: string;
  createdAt?: string;
  hasSnapTrade: boolean;
  subscriptionStatus?: boolean;
  connections?: any[];
  accounts?: any[];
}

export default function SnapTradeAdmin() {
  const [users, setUsers] = useState<SnapUserRow[]>([]);
  const [filtered, setFiltered] = useState<SnapUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [accountData, setAccountData] = useState<Record<string, { balances?: any[]; activities?: any[]; loadingBalances?: boolean; loadingActivities?: boolean }>>({});
  const toast = useToast();

  const display = useCallback((value: any) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") {
      const maybeCode = (value as any).code;
      const maybeName = (value as any).name;
      const maybeId = (value as any).id;
      return maybeCode ?? maybeName ?? maybeId ?? JSON.stringify(value);
    }
    return String(value);
  }, []);

  const displaySymbol = useCallback((value: any) => {
    if (!value) return "-";
    if (typeof value === "string") return value;
    return (
      (value as any).ticker ||
      (value as any).symbol ||
      (value as any).raw_symbol ||
      (value as any)?.underlying_symbol?.symbol ||
      (value as any).description ||
      display(value)
    );
  }, [display]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Request connections by default so we can show counts immediately
      const res = await getSnapTradeUsers(true);
      const data = (res?.data || []) as SnapUserRow[];
      setUsers(data);
      setFiltered(data);
    } catch (e: any) {
      toast({ status: "error", title: e?.message || "Failed to load SnapTrade users" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!search) return setFiltered(users);
    const s = search.toLowerCase();
    setFiltered(users.filter((u) => u.email.toLowerCase().includes(s)));
  }, [search, users]);

  const viewDetail = useCallback(async (userId: number) => {
    try {
      if (expandedUserId === userId) {
        setExpandedUserId(null);
        return;
      }
      const res = await getUserSnapTradeDetail(userId);
      const { connections, accounts } = res.data;
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, connections, accounts } : u))
      );
      setExpandedUserId(userId);
    } catch (e: any) {
      toast({ status: "error", title: e?.message || "Failed to load detail" });
    }
  }, [expandedUserId, toast]);

  const deleteConnection = useCallback(async (userId: number, connectionId: string) => {
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${APIEndpoint}/admin/snaptrade/users/${userId}/connections/${connectionId}`, {
        method: "DELETE",
        headers: { Authorization: token },
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any)?.message || "Failed to delete connection");
      }
      toast({ status: "success", title: "Connection deleted" });
      viewDetail(userId);
    } catch (e: any) {
      toast({ status: "error", title: e?.message || "Failed to delete connection" });
    }
  }, [toast, viewDetail]);

  const refreshConnection = useCallback(async (userId: number, connectionId: string) => {
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${APIEndpoint}/admin/snaptrade/users/${userId}/connections/${connectionId}/refresh`, {
        method: "POST",
        headers: { Authorization: token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to refresh connection");
      toast({ status: "success", title: "Connection refresh requested" });
    } catch (e: any) {
      toast({ status: "error", title: e?.message || "Failed to refresh connection" });
    }
  }, [toast]);

  const deleteAllForUser = useCallback(async (userId: number) => {
    try {
      await deleteUserSnapTrade(userId);
      toast({ status: "success", title: "Deleted user's SnapTrade profile" });
      load();
    } catch (e: any) {
      toast({ status: "error", title: e?.message || "Failed to delete" });
    }
  }, [load, toast]);

  const keyFor = (userId: number, accountId: string) => `${userId}:${accountId}`;

  const loadBalances = useCallback(async (userId: number, accountId: string) => {
    const key = keyFor(userId, accountId);
    setAccountData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loadingBalances: true } }));
    try {
      const res = await getUserAccountBalancesAdmin(userId, accountId);
      const balances = Array.isArray(res?.data) ? res.data : (res as any)?.data?.data ?? [];
      setAccountData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), balances, loadingBalances: false } }));
    } catch (e: any) {
      setAccountData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loadingBalances: false } }));
      toast({ status: "error", title: e?.message || "Failed to load balances" });
    }
  }, [toast]);

  const loadActivities = useCallback(async (userId: number, accountId: string) => {
    const key = keyFor(userId, accountId);
    setAccountData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loadingActivities: true } }));
    try {
      const res = await getUserAccountActivitiesAdmin(userId, accountId, { limit: 50 });
      let list: any = Array.isArray((res as any)?.data)
        ? (res as any).data
        : Array.isArray((res as any)?.data?.data)
          ? (res as any).data.data
          : (res as any)?.data?.data
            ? [(res as any).data.data]
            : [];
      if (!Array.isArray(list) && list) list = [list];
      setAccountData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), activities: list as any[], loadingActivities: false } }));
    } catch (e: any) {
      setAccountData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loadingActivities: false } }));
      toast({ status: "error", title: e?.message || "Failed to load activities" });
    }
  }, [toast]);

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">SnapTrade Users</Heading>
        <HStack>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input placeholder="Search email" value={search} onChange={(e) => setSearch(e.target.value)} />
          </InputGroup>
          <Button size="sm" onClick={load} isLoading={loading}>Refresh</Button>
        </HStack>
      </Flex>

      <Table size="sm" variant="striped" colorScheme="gray">
        <Thead>
          <Tr>
            <Th>Email</Th>
            <Th>Created</Th>
            <Th>Last Active</Th>
            <Th>Subscribed</Th>
            <Th>Connections</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map((u) => (
            <React.Fragment key={u.id}>
              <Tr>
                <Td>{u.email}</Td>
                <Td>{u.createdAt ? moment(u.createdAt).calendar() : "-"}</Td>
                <Td>{u.lastActive ? moment(u.lastActive).fromNow() : "-"}</Td>
                <Td>
                  {u.subscriptionStatus === true ? (
                    <Badge colorScheme="green">subscribed</Badge>
                  ) : u.subscriptionStatus === false ? (
                    <Badge colorScheme="red">not subscribed</Badge>
                  ) : (
                    <Text color="gray.500">unknown</Text>
                  )}
                </Td>
                <Td>
                  {Array.isArray(u.connections) ? (
                    <Badge colorScheme="blue">{u.connections.length} connections</Badge>
                  ) : (
                    <Text color="gray.500">unknown</Text>
                  )}
                </Td>
                <Td>
                  <HStack>
                    <Button size="xs" onClick={() => viewDetail(u.id)}>
                      {expandedUserId === u.id ? "Hide" : "View"}
                    </Button>
                    <IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" onClick={() => deleteAllForUser(u.id)} />
                  </HStack>
                </Td>
              </Tr>
              <Tr>
                <Td colSpan={5}>
                  <Collapse in={expandedUserId === u.id} animateOpacity>
                    <VStack align="start" spacing={4} p={3} bg="var(--bg)" borderRadius={6}>
                      <Heading size="sm">Connections</Heading>
                      {u.connections && u.connections.length > 0 ? (
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>ID</Th>
                              <Th>Brokerage</Th>
                              <Th>Status</Th>
                              <Th>Created</Th>
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {u.connections.map((c: any) => (
                              <Tr key={c.id || c.authorizationId}>
                                <Td>{c.id || c.authorizationId}</Td>
                                <Td>{c.brokerage?.name || c.brokerageName || "-"}</Td>
                                <Td>{c.disabled ? "disabled" : "active"}</Td>
                                <Td>{c.created_at ? moment(c.created_at).calendar() : "-"}</Td>
                                <Td>
                                  <HStack>
                                    <Button size="xs" onClick={() => refreshConnection(u.id, c.id || c.authorizationId)}>Refresh</Button>
                                    <Button size="xs" colorScheme="red" onClick={() => deleteConnection(u.id, c.id || c.authorizationId)}>Delete</Button>
                                  </HStack>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      ) : (
                        <Text color="gray.500">No connections found</Text>
                      )}

                      <Heading size="sm">Accounts</Heading>
                      {u.accounts && u.accounts.length > 0 ? (
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>ID</Th>
                              <Th>Name</Th>
                              <Th>Number</Th>
                              <Th>Type</Th>
                              <Th>Currency</Th>
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {u.accounts.map((a: any) => {
                              const accountId = a.id || a.account_id || a.accountId;
                              const key = accountId ? keyFor(u.id, String(accountId)) : undefined;
                              const data = key ? accountData[key] : undefined;
                              return (
                                <React.Fragment key={accountId}>
                                  <Tr>
                                    <Td>{accountId}</Td>
                                    <Td>{a.name || a.account_name || '-'}</Td>
                                    <Td>{a.number || a.account_number || '-'}</Td>
                                    <Td>{display(a.type || a.account_type)}</Td>
                                    <Td>{display(a.currency || a.account_currency)}</Td>
                                    <Td>
                                      <HStack>
                                        <Button size="xs" isLoading={Boolean(data?.loadingBalances)} onClick={() => accountId && loadBalances(u.id, String(accountId))}>Balances</Button>
                                        <Button size="xs" isLoading={Boolean(data?.loadingActivities)} onClick={() => accountId && loadActivities(u.id, String(accountId))}>Activities</Button>
                                      </HStack>
                                    </Td>
                                  </Tr>
                                  {data?.balances && data.balances.length > 0 && (
                                    <Tr>
                                      <Td colSpan={6}>
                                        <VStack align="start">
                                          <Heading size="xs">Balances</Heading>
                                          <Table size="sm" variant="simple">
                                            <Thead>
                                              <Tr>
                                                <Th>Currency</Th>
                                                <Th>Cash</Th>
                                                <Th>Market Value</Th>
                                                <Th>Total Equity</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              {data.balances.map((b: any, i: number) => (
                                                <Tr key={i}>
                                                  <Td>{display(b.currency || b.symbol)}</Td>
                                                  <Td>{b.cash?.toLocaleString?.() ?? b.cash ?? '-'}</Td>
                                                  <Td>{b.market_value?.toLocaleString?.() ?? b.market_value ?? '-'}</Td>
                                                  <Td>{b.total_equity?.toLocaleString?.() ?? b.total_equity ?? '-'}</Td>
                                                </Tr>
                                              ))}
                                            </Tbody>
                                          </Table>
                                        </VStack>
                                      </Td>
                                    </Tr>
                                  )}
                                  {data?.activities && data.activities.length > 0 && (
                                    <Tr>
                                      <Td colSpan={6}>
                                        <VStack align="start">
                                          <Heading size="xs">Activities</Heading>
                                          <Table size="sm" variant="simple">
                                            <Thead>
                                              <Tr>
                                                <Th>Date</Th>
                                                <Th>Type</Th>
                                                <Th>Symbol</Th>
                                                <Th>Units</Th>
                                                <Th>Price</Th>
                                                <Th>Amount</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              {data.activities.map((t: any, i: number) => {
                                                const date = t.trade_date || t.transaction_date || t.activity_date || t.settlement_date;
                                                return (
                                                  <Tr key={t.id || i}>
                                                    <Td>{date ? moment(date).calendar() : '-'}</Td>
                                                    <Td>{display(t.type || t.activity_type)}</Td>
                                                    <Td>{displaySymbol(t.symbol || t.option_symbol)}</Td>
                                                    <Td>{t.units ?? t.quantity ?? '-'}</Td>
                                                    <Td>{t.price ?? '-'}</Td>
                                                    <Td>{t.amount ?? '-'}</Td>
                                                  </Tr>
                                                );
                                              })}
                                            </Tbody>
                                          </Table>
                                        </VStack>
                                      </Td>
                                    </Tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </Tbody>
                        </Table>
                      ) : (
                        <Text color="gray.500">No accounts found</Text>
                      )}
                    </VStack>
                  </Collapse>
                </Td>
              </Tr>
            </React.Fragment>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

